import { Flex, Skeleton, Text } from "@chakra-ui/react"
import { DataSource, Graph, TimeRange, Timeseries, Toggles } from "@hydrologiq/schemas/1.0.0/installationPage"
import { ApolloError, useLazyQuery, useQuery } from "@apollo/client"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import {
  RangeResolution,
  rangeFilter,
  rangeInMS,
} from "@hydrologiq/shared/monitoring/builders/1.0.0/graphs/rangeFilter"
import { IANATimeZone, nowDateTime, toTimestamp, toUserTZ } from "@hydrologiq/common/helpers/date"
import TimeSeriesGraphImpl, {
  TimeSeriesDataPoint,
  TimeSeriesSeries,
  AssetConfig,
  getSeriesColor,
  generatePeakColor,
} from "./TimeSeries"
import { DateTime, milliseconds, seconds } from "timezonecomplete"
import { useEffect, useState, useMemo } from "react"
import { TimePoint, convertTimeSeriesData } from "@hydrologiq/common/types/graph/timeseries"
import { energy } from "@hydrologiq/common/transformations/data/energy"
import { EnergyUnit } from "@hydrologiq/common/types/energy/consumption"
import { getQuery, getQueryRaw } from "@hydrologiq/shared/monitoring/graphing/queries"

interface TimeSeriesGraphProps {
  graph: Graph
  timeseries: Timeseries
  sourceRange: TimeRange
  installationId: string
  installationEndDate: DateTime
  installationStartDate: DateTime
  assetConfigs?: AssetConfig[] // Custom asset configurations from system
}

type TimeSeriesGraphImplProps = {
  graph: Graph
  timeseries: Timeseries
  range: string
  installationId: string
  installationStartDate: DateTime
  endDate: DateTime
  start: DateTime
  stop: DateTime
  assetConfigs?: AssetConfig[]
}

type TimeSeriesGraphRawProps = {} & TimeSeriesGraphImplProps

const processTimeSeriesData = (
  data:
    | gqlTypes.GetInstallationTotalQuery
    | gqlTypes.InstallationPowerAverageQuery
    | gqlTypes.InstallationPowerCombinedQuery
    | gqlTypes.InstallationWeightCombinedQuery
    | gqlTypes.InstallationDataWindowedQuery
    | gqlTypes.InstallationDataWindowedCombinedQuery
    | gqlTypes.InstallationHydrogenWindowedMwQuery
    | undefined,
  graph: Graph,
  comparison?: { label: string; data: gqlTypes.TimeSeriesData[] },
  _toggles?: Toggles, // Kept for backward compatibility but no longer used for peak control
  assetConfigs?: AssetConfig[]
): {
  processedData: any[]
  series: any[]
} => {
  if (!data?.values) return { processedData: [], series: [] }

  // Check for multiple assets first (original v2.0.0 logic)
  const assets = Array.from(new Set(data.values?.map((value: any) => value.asset ?? value.field ?? "")))
  const useAssets = (assets ?? []).length > 1

  // If we have multiple assets, use the simpler multi-asset approach
  if (useAssets) {
    let values =
      data.values
        ?.filter((value: any) => !Number.isNaN(value?.value) && value.asset)
        .map((value: any) => {
          // Apply source transformation (energy unit conversion) per asset
          let transformedValue = value.value
          if (graph.source.sourceUnit && graph.source.desiredUnit) {
            transformedValue = energy(
              graph.source.sourceUnit as EnergyUnit,
              graph.source.desiredUnit as EnergyUnit,
              value.value
            )
          }

          return {
            timestamp: toUserTZ(value.time ?? "").toIsoString(),
            [value.asset
              ? assetConfigs?.find((config) => config.assetId === value.asset)
                ? value.asset
                : "no_data"
              : "value"]: transformedValue,
          }
        })
        .filter((value: any) => !value["no_data"]) ?? []

    // Group values by timestamp and populate the remaining object values
    const groupedValues = values.reduce((acc: any, value: any) => {
      acc[value.timestamp] = acc[value.timestamp] || { timestamp: value.timestamp }
      acc[value.timestamp] = { ...acc[value.timestamp], ...value }
      return acc
    }, {} as Record<string, TimeSeriesDataPoint>)

    // Handle peak data for each asset (always process if available)
    const peaksLabel = comparison ? comparison.label : "Peak"
    let peakAssets: string[] = []

    // Always process peaks if they exist in the data
    let peakData: gqlTypes.TimeSeriesData[] = []

    // Get peak data from either data.peaks or comparison
    if ("peaks" in data && data.peaks) {
      peakData = data.peaks as gqlTypes.TimeSeriesData[]
    } else if (comparison) {
      peakData = comparison.data
    }

    if (peakData.length > 0) {
      // Process peak data per asset
      const peakValues =
        peakData
          ?.filter((value: any) => !Number.isNaN(value?.value) && value.asset)
          .map((value: any) => {
            // Apply source transformation (energy unit conversion) to peak data
            let transformedValue = value.value
            if (graph.source.sourceUnit && graph.source.desiredUnit) {
              transformedValue = energy(
                graph.source.sourceUnit as EnergyUnit,
                graph.source.desiredUnit as EnergyUnit,
                value.value
              )
            }

            return {
              timestamp: toUserTZ(value.time ?? "").toIsoString(),
              [`${value.asset}_peaks`]: transformedValue,
              asset: value.asset,
            }
          }) ?? []

      // Track which assets have peak data
      peakAssets = Array.from(new Set(peakValues.map((v: any) => v.asset)))

      // Add peak data to grouped values
      peakValues.forEach((peakValue: any) => {
        const existing = groupedValues[peakValue.timestamp] || { timestamp: peakValue.timestamp }
        groupedValues[peakValue.timestamp] = {
          ...existing,
          [`${peakValue.asset}_peaks`]: peakValue[`${peakValue.asset}_peaks`],
        }
      })
    }

    const processedData = (Object.values(groupedValues) as TimeSeriesDataPoint[]).sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )

    // Create series for main asset data
    const mainSeries: TimeSeriesSeries<readonly string[]>[] = (
      assets
        ?.filter((asset) => values.some((value: any) => value[asset as string] !== undefined))
        .map((asset, index) => {
          // Check if we have custom configuration for this asset
          const customConfig = assetConfigs?.find((config) => config.assetId === asset)

          if (customConfig) {
            return {
              name: asset as string,
              color: customConfig.color,
              label: customConfig.label,
              strokeWidth: customConfig.strokeWidth ?? 2,
              strokeDasharray: customConfig.strokeDasharray,
              dot: customConfig.dot ?? false,
              connectNulls: customConfig.connectNulls ?? true,
              type: "monotone" as const,
              group: customConfig?.group || undefined,
            }
          }

          // Fallback to auto-generated configuration
          return {
            name: asset as string,
            color: getSeriesColor(index, assets?.length || 1),
            label: asset as string,
            strokeWidth: 2,
            dot: false,
            connectNulls: true,
            type: "monotone" as const,
          }
        }) ?? []
    ).sort((a, b) => a.label.localeCompare(b.label))

    // Create series for peak data (if available and enabled per asset)
    const peakSeries: TimeSeriesSeries<readonly string[]>[] = []
    if (peakAssets.length > 0) {
      peakAssets.forEach((asset) => {
        const customConfig = assetConfigs?.find((config) => config.assetId === asset)
        const mainSeriesIndex = assets?.indexOf(asset) ?? 0

        // Check if peaks should be shown for this asset (default to true)
        const shouldShowPeaks = customConfig?.showPeaks !== false

        if (shouldShowPeaks) {
          const assetColor = customConfig?.color || getSeriesColor(mainSeriesIndex, assets?.length || 1)
          const peakColor = generatePeakColor(assetColor, mainSeriesIndex, assets?.length || 1)

          peakSeries.push({
            name: `${asset}_peaks`,
            color: peakColor,
            label: `${customConfig?.label || asset} ${peaksLabel}`,
            strokeWidth: 2,
            strokeDasharray: "5 5", // Dashed line for peaks
            dot: false,
            connectNulls: true,
            type: "monotone" as const,
            group: customConfig?.group || undefined,
          })
        }
      })
    }

    // Combine main series and peak series
    const allSeries = [...mainSeries, ...peakSeries].sort((a, b) => a.label.localeCompare(b.label))

    return { processedData, series: allSeries }
  }

  // Single asset or complex processing (enhanced v1.0.0 logic)
  const now = nowDateTime()
  let points: TimePoint[] = []
  let peaks: TimePoint[] = []
  let minWeights: TimePoint[] = []

  // Convert main data
  if (data.values) {
    points = convertTimeSeriesData(data.values as gqlTypes.TimeSeriesData[])
  }

  // Convert peaks/comparison data (always process if available)
  const peaksLabel = comparison ? comparison.label : "Peak"

  // Always process peaks if they exist in the data
  if ("peaks" in data && data.peaks) {
    peaks = convertTimeSeriesData(data.peaks as gqlTypes.TimeSeriesData[])
  } else if (comparison) {
    peaks = convertTimeSeriesData(comparison.data)
  }

  // Handle minimum weights for specific data types
  if ("mw" in data && data.mw) {
    minWeights = convertTimeSeriesData(data.mw as gqlTypes.TimeSeriesData[])
    // Process minimum weights as in v1.0.0
    let weights: TimePoint[] = []
    minWeights.forEach((value, index) => {
      const next = index < minWeights.length ? minWeights[index + 1] : undefined
      weights.push(value)
      weights.push({
        ...value,
        time: next ? next.time.sub(seconds(1)) : now.add(seconds(60)),
      })
    })
    minWeights = weights

    // Apply refueling logic for zero weight periods
    const refuellingGroups: [DateTime, TimePoint | undefined][] = []
    minWeights.forEach((minWeight) => {
      const time = minWeight.time
      if (
        refuellingGroups.filter(
          (value) => value[1] && ((time.unixUtcMillis() - value[1].time.unixUtcMillis()) * -1) / 1000 / 60 > 60
        ).length == 0
      ) {
        const matchingWeight = minWeights.filter((value) => {
          const timeDiff = ((time.unixUtcMillis() - value.time.unixUtcMillis()) * -1) / 1000 / 60
          return time !== value.time && timeDiff > 60 && timeDiff < 60 * 24
        })
        if (matchingWeight.length > 0)
          refuellingGroups.push([time, matchingWeight.length > 0 ? matchingWeight[0] : undefined])
      }
    })

    const zeroValueRefuellingGroups = refuellingGroups
      .filter((value) => value[1] && value[1].value == 0)
      .map((value) => [value[0], value[1]?.time])

    points = points.map((point, index) => {
      const groups = zeroValueRefuellingGroups.filter(
        (group) =>
          group[0] &&
          group[1] &&
          point.time.unixUtcMillis() >= group[0].unixUtcMillis() &&
          point.time.unixUtcMillis() <= group[1].unixUtcMillis() + 60 * 1000 * 60
      )
      if (groups.length != 0) {
        if (index != 0) point.value = index == 0 ? 0 : points[index - 1].value
      }
      return point
    })
  }

  // Apply energy unit conversions
  if (graph.source.sourceUnit && graph.source.desiredUnit) {
    points.forEach((value, index, arr) => {
      const newVal = energy(graph.source.sourceUnit as EnergyUnit, graph.source.desiredUnit as EnergyUnit, value.value)
      arr[index].value = newVal
    })
    console.log(points)
    if (peaks.length > 0) {
      peaks.forEach((value, index, arr) => {
        const newVal = energy(
          graph.source.sourceUnit as EnergyUnit,
          graph.source.desiredUnit as EnergyUnit,
          value.value
        )
        arr[index].value = newVal
      })
    }
  }

  // Create time-indexed data structure for Recharts
  const timeMap = new Map<string, TimeSeriesDataPoint>()

  // Add main points
  points.forEach((point) => {
    const timestamp = toUserTZ(point.time).toIsoString()
    timeMap.set(timestamp, {
      timestamp,
      time: point.time,
      points: point.value,
    })
  })

  // Add peaks if available
  if (peaks.length > 0) {
    peaks.forEach((point) => {
      const timestamp = toUserTZ(point.time).toIsoString()
      const existing = timeMap.get(timestamp) || {
        timestamp,
        time: point.time,
        points: null,
      }
      timeMap.set(timestamp, {
        ...existing,
        [peaksLabel.toLowerCase()]: point.value,
      })
    })
  }

  // Add minimum weights
  if (minWeights.length > 0) {
    minWeights.forEach((point) => {
      const timestamp = toUserTZ(point.time).toIsoString()
      const existing = timeMap.get(timestamp) || {
        timestamp,
        time: point.time,
        points: null,
      }
      timeMap.set(timestamp, {
        ...existing,
        minimum_weight: point.value,
      })
    })
  }

  // Convert to array and sort by timestamp
  const processedData = Array.from(timeMap.values()).sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  )

  // Extract asset name for single asset scenarios
  const singleAssetName = data.values?.[0]?.asset || data.values?.[0]?.field || "Data"
  const singleAssetLabel = assetConfigs?.find((config) => config.assetId === singleAssetName)?.label || singleAssetName

  // Define series
  const series: TimeSeriesSeries<readonly string[]>[] = [
    {
      name: "points",
      color: getSeriesColor(0, 3), // Use first color from palette
      label: singleAssetLabel,
      strokeWidth: 2,
      dot: false,
      connectNulls: true,
      type: "monotone",
    },
  ]

  // Check if peaks should be shown for single asset (default to true)
  const singleAssetConfig = assetConfigs?.find((config) => config.assetId === singleAssetName)
  const shouldShowSingleAssetPeaks = singleAssetConfig?.showPeaks !== false

  if (peaks.length > 0 && shouldShowSingleAssetPeaks) {
    const assetColor = singleAssetConfig?.color || getSeriesColor(0, 1)
    const peakColor = generatePeakColor(assetColor, 0, 1)

    series.push({
      name: peaksLabel.toLowerCase(),
      color: peakColor,
      label: `${singleAssetLabel} ${peaksLabel}`,
      strokeWidth: 2,
      dot: false,
      connectNulls: true,
      strokeDasharray: "5 5",
      type: "monotone",
    })
  }

  if (minWeights.length > 0) {
    series.push({
      name: "minimum_weight",
      color: getSeriesColor(2, 3), // Use third color for minimum weight
      label: "Minimum weight",
      strokeWidth: 1,
      dot: false,
      connectNulls: false,
      type: "step",
    })
  }

  return { processedData, series }
}

function TimeSeriesGraphRaw({
  graph,
  timeseries,
  range,
  installationId,
  installationStartDate,
  endDate,
  start,
  stop,
  assetConfigs,
}: TimeSeriesGraphRawProps) {
  const [toggles] = useState<Toggles | undefined>(graph.toggles && structuredClone(graph.toggles))
  const [loaded, setLoaded] = useState<boolean>(false)

  const vars: Record<string, any> = {
    field: graph.source.field,
    start: toTimestamp(start),
    stop: toTimestamp(stop),
    id: installationId,
    window: range,
    installationStart: toTimestamp(installationStartDate),
    location: IANATimeZone(),
    combine: false,
  }

  let baseVars = { ...vars }

  switch (graph.source.type) {
    case "InfluXDB":
      if (graph.source.influx?.bucket) baseVars["bucket"] = graph.source.influx.bucket
      if (graph.source.influx?.measurement) baseVars["measurement"] = graph.source.influx.measurement
      break
  }

  const {
    loading,
    error,
    data,
  }: {
    loading: boolean
    error?: ApolloError | undefined
    data:
      | gqlTypes.InstallationDataWindowedQuery
      | gqlTypes.InstallationDataWindowedCombinedQuery
      | gqlTypes.InstallationHydrogenWindowedMwQuery
      | undefined
  } = useQuery(getQueryRaw(toggles, graph.source.field, graph.source.method), {
    // @ts-ignore
    variables: baseVars,
  })

  const [dataComparison, setDataComparison] = useState<gqlTypes.TimeSeriesData[] | undefined>(undefined)

  const [getInstallationData, { loading: loadingAdditional }] = useLazyQuery(gqlTypes.InstallationDataWindowedDocument)
  const sourceComparison = graph.sourceComparison as DataSource

  useEffect(() => {
    if (sourceComparison) {
      if (!sourceComparison || !sourceComparison.field) return
      let varsAdditional = { ...baseVars }
      switch (sourceComparison.type) {
        case "InfluXDB":
          if (sourceComparison.influx?.bucket) varsAdditional["bucket"] = sourceComparison.influx.bucket
          if (sourceComparison.influx?.measurement) varsAdditional["measurement"] = sourceComparison.influx.measurement
          break
      }
      getInstallationData({
        // @ts-ignore
        variables: { ...varsAdditional, field: sourceComparison.field },
      }).then(({ data }) => {
        if (data && data.values) setDataComparison(data.values as gqlTypes.TimeSeriesData[])
      })
    }
  }, [graph.sourceComparison])

  useEffect(() => {
    if (loaded) {
      // Callback when chart is loaded
    }
  }, [loaded])

  if (loadingAdditional || loading) {
    return (
      <Flex height="530px">
        <Skeleton height="100%" width="100%" />
      </Flex>
    )
  }

  if (error) {
    return <Text>Error: {error.message}</Text>
  }

  if (!data) {
    return <Text>Error: invalid data format</Text>
  }

  const comparison =
    dataComparison && graph.sourceComparison && sourceComparison.label
      ? { label: sourceComparison.label, data: dataComparison }
      : undefined

  const { processedData, series } = processTimeSeriesData(data, graph, comparison, toggles, assetConfigs)

  return (
    <>
      <TimeSeriesGraphImpl
        data={processedData}
        series={series}
        graph={graph}
        timeseries={timeseries}
        endDate={endDate}
        startDate={start}
        comparison={comparison}
        height="530px"
        onLoaded={() => setLoaded(true)}
        timeAxisFormat={{
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }}
        valueAxisFormat={{
          maximumFractionDigits: 1,
        }}
      />
    </>
  )
}

function TimeSeriesGraphDownSampled({
  graph,
  timeseries,
  range,
  installationId,
  installationStartDate,
  endDate,
  start,
  stop,
  assetConfigs,
}: TimeSeriesGraphRawProps) {
  const [toggles] = useState<Toggles | undefined>(graph.toggles && structuredClone(graph.toggles))
  const [_, setLoaded] = useState<boolean>(false)

  const baseVars: any = {
    start: toTimestamp(start),
    stop: toTimestamp(stop),
    id: installationId,
    granularity: range,
    location: IANATimeZone(),
  }
  let vars = { ...baseVars }
  if (graph.source.field_downsampled) vars["field"] = graph.source.field_downsampled

  const field = graph.source.field_downsampled || graph.source.field
  if (field == "weight") vars["installationStart"] = toTimestamp(installationStartDate)

  const {
    loading,
    error,
    data,
  }: {
    loading: boolean
    error?: ApolloError | undefined
    data:
      | gqlTypes.GetInstallationTotalQuery
      | gqlTypes.InstallationPowerAverageQuery
      | gqlTypes.InstallationPowerCombinedQuery
      | gqlTypes.InstallationWeightCombinedQuery
      | undefined
  } = useQuery(getQuery(toggles, field, graph.source.method), {
    variables: vars,
  })

  const [dataComparison, setDataComparison] = useState<gqlTypes.TimeSeriesData[] | undefined>(undefined)

  const [getInstallationData, { loading: loadingAdditional }] = useLazyQuery(gqlTypes.InstallationDataWindowedDocument)
  const sourceComparison = graph.sourceComparison as DataSource

  useEffect(() => {
    if (sourceComparison) {
      if (!sourceComparison || !sourceComparison.field) return
      let varsAdditional = { ...baseVars }
      switch (sourceComparison.type) {
        case "InfluXDB":
          if (sourceComparison.influx?.bucket) varsAdditional["bucket"] = sourceComparison.influx.bucket
          if (sourceComparison.influx?.measurement) varsAdditional["measurement"] = sourceComparison.influx.measurement
          break
      }
      getInstallationData({
        // @ts-ignore
        variables: { ...varsAdditional, field: sourceComparison.field },
      }).then(({ data }) => {
        if (data && data.values) setDataComparison(data.values as gqlTypes.TimeSeriesData[])
      })
    }
  }, [graph.sourceComparison])

  if (loading || loadingAdditional) {
    return (
      <Flex height="530px">
        <Skeleton height="100%" width="100%" />
      </Flex>
    )
  }

  if (error) {
    return <Text>Error: {error.message}</Text>
  }

  if (!data) {
    return <Text>Error: invalid data format</Text>
  }

  const comparison =
    dataComparison && graph.sourceComparison && sourceComparison.label
      ? { label: sourceComparison.label, data: dataComparison }
      : undefined

  const { processedData, series } = processTimeSeriesData(data, graph, comparison, toggles, assetConfigs)

  return (
    <>
      <TimeSeriesGraphImpl
        data={processedData}
        series={series}
        graph={graph}
        timeseries={timeseries}
        endDate={endDate}
        startDate={start}
        comparison={comparison}
        height="530px"
        onLoaded={() => setLoaded(true)}
        timeAxisFormat={{
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }}
        valueAxisFormat={{
          maximumFractionDigits: 1,
        }}
      />
    </>
  )
}

function TimeSeriesGraph({
  graph,
  timeseries,
  sourceRange,
  installationId,
  installationEndDate,
  installationStartDate,
  assetConfigs,
}: TimeSeriesGraphProps) {
  const endDate = useMemo(() => {
    const dateNow = nowDateTime()
    return dateNow.unixUtcMillis() > installationEndDate.unixUtcMillis() ? installationEndDate : dateNow
  }, [installationEndDate])

  const rangeMS = rangeInMS(sourceRange.quantity, sourceRange.resolution as RangeResolution)

  const stop = useMemo(() => endDate, [endDate])
  const start = useMemo(() => {
    const calculatedStart = endDate.clone().sub(milliseconds(rangeMS))
    return calculatedStart.unixUtcMillis() < installationStartDate.unixUtcMillis()
      ? installationStartDate
      : calculatedStart
  }, [endDate, rangeMS, installationStartDate])
  const range = timeseries.interval
    ? rangeFilter(timeseries.interval.quantity, timeseries.interval.resolution as RangeResolution)
    : "30s"
  const windowMS = timeseries.interval
    ? rangeInMS(timeseries.interval.quantity, timeseries.interval.resolution as RangeResolution)
    : rangeInMS(30, "second")
  const downsampled =
    !graph.isBeta &&
    windowMS >= 5 * 60000 &&
    (["weight", "power"].includes(graph.source.field) || graph.source.field_downsampled)

  return downsampled ? (
    <TimeSeriesGraphDownSampled
      endDate={endDate}
      graph={graph}
      installationId={installationId}
      installationStartDate={installationStartDate}
      range={range}
      start={start}
      stop={stop}
      timeseries={timeseries}
      assetConfigs={assetConfigs}
    />
  ) : (
    <TimeSeriesGraphRaw
      endDate={endDate}
      graph={graph}
      installationId={installationId}
      installationStartDate={installationStartDate}
      range={range}
      start={start}
      stop={stop}
      timeseries={timeseries}
      assetConfigs={assetConfigs}
    />
  )
}

export default TimeSeriesGraph
